import { formatHuf } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@kgc/ui';
import { Info } from 'lucide-react';
import type { BreakdownDay, CartPricing } from '../types';

const DAY_NAMES_HU = ['V', 'H', 'K', 'Sze', 'Cs', 'P', 'Szo'];
const DAY_NAMES_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const DAY_NAMES_UK = ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

const DAY_NAMES: Record<string, string[]> = {
  hu: DAY_NAMES_HU,
  en: DAY_NAMES_EN,
  de: DAY_NAMES_DE,
  uk: DAY_NAMES_UK,
};

const TYPE_KEYS: Record<BreakdownDay['type'], string> = {
  WORKDAY: 'rental.wizard.breakdownWorkday',
  SUNDAY: 'rental.wizard.breakdownSunday',
  HOLIDAY: 'rental.wizard.breakdownHoliday',
};

const VALUE_KEYS: Record<string, string> = {
  '1': 'rental.wizard.breakdownFullDay',
  '0.5': 'rental.wizard.breakdownHalfDay',
};

function BreakdownRow({
  day,
  dayNames,
  t,
}: {
  day: BreakdownDay;
  dayNames: string[];
  t: (key: string, params?: Record<string, string>) => string;
}) {
  const dayName = dayNames[day.dayOfWeek] ?? '';
  const isDiscount = day.value < 1;
  const rowClass = isDiscount ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground';

  return (
    <tr className={rowClass}>
      <td className="py-1 pr-3 text-sm tabular-nums">{day.date}</td>
      <td className="py-1 pr-3 text-sm">{dayName}</td>
      <td className="py-1 pr-3 text-sm">
        {t(TYPE_KEYS[day.type] ?? 'rental.wizard.breakdownWorkday')}
        {day.holidayName != null && <span className="ml-1 text-xs">({day.holidayName})</span>}
      </td>
      <td className="py-1 text-right text-sm font-medium">
        {t(VALUE_KEYS[String(day.value)] ?? String(day.value))}
      </td>
    </tr>
  );
}

interface PriceBreakdownDialogProps {
  pricing: CartPricing;
}

export function PriceBreakdownDialog({ pricing }: PriceBreakdownDialogProps) {
  const { t, locale } = useTranslation();
  const dayNames = DAY_NAMES[locale] ?? DAY_NAMES_HU;
  const dailyRate = pricing.items[0]?.dailyRate ?? 0;

  const rawBillable = pricing.breakdownDays.reduce((sum, d) => sum + d.value, 0);
  const payableDays =
    pricing.weeklyDiscountDays > 0
      ? Math.max(0.5, rawBillable - pricing.weeklyDiscountDays)
      : rawBillable;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="ml-2 inline-flex items-center rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={t('rental.wizard.breakdownTitle')}
        >
          <Info className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('rental.wizard.breakdownTitle')}</DialogTitle>
        </DialogHeader>

        {pricing.isMonthlyMode ? (
          <div className="space-y-3 py-4">
            <p className="text-sm text-muted-foreground">
              {t('rental.wizard.breakdownMonthlyNote', {
                days: String(pricing.durationDays),
                billable: String(pricing.billableDays),
              })}
            </p>
            <div className="rounded-lg bg-muted p-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('rental.wizard.breakdownDailyRate')}
                </span>
                <span className="font-medium text-foreground">{formatHuf(dailyRate)}</span>
              </div>
              <div className="flex justify-between text-sm font-semibold">
                <span className="text-foreground">{t('rental.wizard.netRentalFee')}</span>
                <span className="text-primary">{formatHuf(pricing.subtotal)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="max-h-[40vh] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-background">
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="pb-2 pr-3 font-medium">{t('rental.wizard.breakdownDate')}</th>
                    <th className="pb-2 pr-3 font-medium">{t('rental.wizard.breakdownDay')}</th>
                    <th className="pb-2 pr-3 font-medium">{t('rental.wizard.breakdownType')}</th>
                    <th className="pb-2 text-right font-medium">
                      {t('rental.wizard.breakdownValue')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pricing.breakdownDays.map(day => (
                    <BreakdownRow key={day.date} day={day} dayNames={dayNames} t={t} />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rounded-lg bg-muted p-3 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('rental.wizard.breakdownCalendarDays')}
                </span>
                <span className="text-foreground">{pricing.durationDays}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('rental.wizard.breakdownBillableDays')}
                </span>
                <span className="text-foreground">{rawBillable}</span>
              </div>
              {pricing.weeklyDiscountDays > 0 && (
                <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                  <span>
                    {t('rental.wizard.breakdownWeeklyDiscount', {
                      days: String(pricing.weeklyDiscountDays),
                    })}
                  </span>
                  <span>-{pricing.weeklyDiscountDays}</span>
                </div>
              )}
              <hr className="border-border" />
              <div className="flex justify-between text-sm font-semibold">
                <span className="text-foreground">{t('rental.wizard.breakdownPayableDays')}</span>
                <span className="text-primary">{payableDays}</span>
              </div>
              <hr className="border-border" />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('rental.wizard.netRentalFee')}</span>
                <span className="font-medium text-foreground">{formatHuf(pricing.subtotal)}</span>
              </div>
              {pricing.discountTotal > 0 && (
                <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                  <span>{t('rental.wizard.discount')}</span>
                  <span>-{formatHuf(pricing.discountTotal)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('rental.wizard.vat27')}</span>
                <span className="text-foreground">{formatHuf(pricing.vatAmount)}</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span className="text-foreground">{t('rental.wizard.rentalFeeTotal')}</span>
                <span className="text-primary">{formatHuf(pricing.grandTotal)}</span>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
